import React from 'react';
import { Box, Button, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

/**
 * Collapsed one-line state of the onboarding checklist card.
 *
 * The expander is an icon button rather than a labelled one: with the "Jatka: <step>"
 * link, a progress bar and the hide action, a third labelled button pushes the row past
 * the dashboard's content width and wraps it onto two lines.
 */
function ChecklistSlimBar({
  title,
  doneCount,
  totalCount,
  nextStepTitle,
  calm,
  progressLabel,
  onExpand,
  onHide,
  expandLabel,
  hideLabel,
  continueLabel,
}) {
  const Icon = calm ? AutoAwesomeIcon : RocketLaunchIcon;

  return (
    <Paper
      elevation={0}
      data-testid="onboarding-checklist-slim"
      sx={{
        mb: 3,
        px: { xs: 2, sm: 2.5 },
        py: 1.5,
        border: '1px solid',
        borderColor: calm ? 'divider' : 'rgba(49, 130, 206, 0.35)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Icon sx={{ fontSize: 20, flexShrink: 0, color: calm ? 'text.secondary' : 'secondary.main' }} />
        <Typography sx={{ fontSize: '14.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>{title}</Typography>
        <Box sx={{ width: 120, flexShrink: 0, minWidth: 80 }}>
          <LinearProgress
            variant="determinate"
            value={totalCount ? (doneCount / totalCount) * 100 : 0}
            color="secondary"
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {progressLabel}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 8 }} />

        {/* Actions stay one group so they never split across rows. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {nextStepTitle && (
            <Button
              size="small"
              variant="text"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={onExpand}
              sx={{ minWidth: 0, maxWidth: 260, textTransform: 'none' }}
              data-testid="onboarding-checklist-continue"
            >
              <Box
                component="span"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
              >
                {continueLabel}
              </Box>
            </Button>
          )}
          <Tooltip title={expandLabel}>
            <IconButton
              size="small"
              onClick={onExpand}
              aria-label={expandLabel}
              sx={{ flexShrink: 0, color: 'text.secondary' }}
              data-testid="onboarding-checklist-expand"
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={onHide}
            sx={{ flexShrink: 0, textTransform: 'none', whiteSpace: 'nowrap', color: 'text.disabled' }}
            data-testid="onboarding-checklist-hide"
          >
            {hideLabel}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default ChecklistSlimBar;
